import Button from '@/components/base/Button';
import StatusBadge from '@/components/base/StatusBadge';
import { formatDate, formatMoney } from '@/utils/format';
import { needsAttention } from '@/utils/wallet';
import type { Wallet, WalletTopUp } from '@/types/portal';
import type { WalletAction } from './WalletManageModal';

interface WalletDetailRowProps {
  wallet: Wallet;
  topUps: WalletTopUp[];
  expanded: boolean;
  onToggle: () => void;
  canFund: boolean;
  onTopUp: () => void;
  canManage: boolean;
  onManage: (action: WalletAction) => void;
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-background-200 bg-background-50 px-3 py-2">
      <p className="text-[10px] font-label uppercase tracking-wider text-foreground-400">{label}</p>
      <p className="mt-0.5 truncate text-sm text-foreground-900">{value}</p>
    </div>
  );
}

export default function WalletDetailRow({
  wallet,
  topUps,
  expanded,
  onToggle,
  canFund,
  onTopUp,
  canManage,
  onManage,
}: WalletDetailRowProps) {
  const inactive = wallet.status === 'Wallet inactive';
  const settled = topUps.filter((entry) => entry.status === 'Completed');

  return (
    <div className={expanded ? 'bg-background-100/50' : ''}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-background-100"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-[11px] font-semibold text-secondary-800">
          {wallet.currency}
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground-900">{wallet.ownerName}</p>
          <p className="truncate text-xs text-foreground-500">{wallet.market}</p>
        </div>

        <div className="hidden shrink-0 text-right sm:block">
          <p className="font-heading text-sm font-semibold text-foreground-950">
            {formatMoney(wallet.balance, wallet.currency)}
          </p>
          <p className="text-[11px] text-foreground-400">
            {topUps.length > 0 ? `${topUps.length} top-ups` : 'Balance'}
          </p>
        </div>

        <div className="hidden shrink-0 md:block">
          {wallet.pending > 0 ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-secondary-800">
              <i className="ri-time-line" />
              {formatMoney(wallet.pending, wallet.currency)} in flight
            </span>
          ) : needsAttention(wallet) ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-primary-700">
              <i className="ri-alert-line" />
              Needs attention
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] text-accent-700">
              <i className="ri-checkbox-circle-line" />
              Funding ready
            </span>
          )}
        </div>

        <StatusBadge status={wallet.status} className="shrink-0" />

        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-lg text-foreground-400">
          <i className={`transition-transform ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
        </span>
      </button>

      {expanded ? (
        <div className="border-t border-background-200 px-5 py-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <DetailField label="Balance" value={formatMoney(wallet.balance, wallet.currency)} />
            <DetailField label="Currency" value={wallet.currency} />
            <DetailField label="Market" value={wallet.market} />
            <DetailField label="Last activity" value={formatDate(wallet.lastActivity)} />
          </div>

          {wallet.description ? (
            <p className="mt-3 text-sm text-foreground-600">{wallet.description}</p>
          ) : null}

          {canManage ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" icon="ri-edit-line" onClick={() => onManage('edit')}>
                Edit details
              </Button>
              <Button
                size="sm"
                variant="outline"
                icon={inactive ? 'ri-play-circle-line' : 'ri-pause-circle-line'}
                onClick={() => onManage(inactive ? 'activate' : 'deactivate')}
              >
                {inactive ? 'Activate' : 'Deactivate'}
              </Button>
              <Button size="sm" variant="ghost" icon="ri-delete-bin-line" onClick={() => onManage('delete')}>
                Delete
              </Button>
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <h4 className="inline-flex items-center gap-2 font-heading text-xs font-semibold uppercase tracking-wider text-foreground-500">
              <i className="ri-history-line" />
              Top-up ledger
              <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal text-secondary-800">
                {topUps.length}
              </span>
              {settled.length > 0 ? (
                <span className="hidden text-[11px] font-normal normal-case tracking-normal text-foreground-400 sm:inline">
                  {formatMoney(
                    settled.reduce((sum, entry) => sum + entry.amount, 0),
                    wallet.currency,
                  )}{' '}
                  settled
                </span>
              ) : null}
            </h4>
            <Button size="sm" icon="ri-add-line" disabled={!canFund} onClick={onTopUp}>
              Fund wallet
            </Button>
          </div>

          {topUps.length === 0 ? (
            <div className="mt-3 rounded-md border border-dashed border-background-300 bg-background-50 px-4 py-8 text-center">
              <span className="mx-auto flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-base text-foreground-400">
                <i className="ri-inbox-line" />
              </span>
              <p className="mt-2 text-sm text-foreground-600">
                No top-ups recorded for this wallet yet.
              </p>
              <p className="mt-0.5 text-xs text-foreground-400">
                Funding this wallet will start its top-up history.
              </p>
            </div>
          ) : (
            <ul className="mt-3 space-y-2">
              {topUps.map((entry) => (
                <li
                  key={entry.id}
                  className="grid grid-cols-1 gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:items-center sm:gap-4"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-foreground-900">{entry.method}</p>
                    <p className="truncate text-[11px] text-foreground-400">
                      {entry.reference} · {entry.by}
                    </p>
                  </div>
                  <span className="text-xs text-foreground-500">{formatDate(entry.createdAt)}</span>
                  <span className="font-heading text-sm font-semibold text-foreground-950">
                    {formatMoney(entry.amount, entry.currency)}
                  </span>
                  <StatusBadge status={entry.status} className="justify-self-start sm:justify-self-end" />
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}